/**
 * Tenant Detection Utility
 *
 * Extrait le slug du tenant depuis le subdomain pour le branding/UX uniquement.
 *
 * IMPORTANT:
 * - La logique de sécurité tenant se fait côté backend via JWT (organizationSlug)
 * - Le subdomain frontend sert UNIQUEMENT au branding (logo, thème, texte)
 * - Pas de validation de sécurité ici
 *
 * Architecture:
 * - terrilink.io → Landing page
 * - www.terrilink.io → Landing page
 * - app.terrilink.io → Application principale
 * - dev.terrilink.io → Environnement de dev
 * - demo.terrilink.io → Démonstration
 * - <slug>.terrilink.io → Organisation tenant (branding custom)
 *
 * @module utils/tenant
 */

/**
 * Domaine de base de la plateforme
 * @constant {string}
 */
const BASE_DOMAIN = 'terrilink.io'

/**
 * Subdomains réservés (non-tenants)
 * @constant {string[]}
 */
const RESERVED_SUBDOMAINS = ['www', 'app', 'api', 'dev', 'demo', 'admin', 'staging', 'localhost']

/**
 * Extrait le slug du tenant depuis le subdomain
 *
 * Exemples:
 * - aveyronworldwide.terrilink.io → "aveyronworldwide"
 * - app.terrilink.io → null (subdomain réservé)
 * - terrilink.io → null (pas de subdomain)
 * - localhost → null (développement local)
 *
 * @returns {string|null} Le slug du tenant ou null si pas de tenant
 */
export function getTenantSlug() {
  const hostname = window.location.hostname

  // Développement local
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // En dev, on peut forcer un tenant via localStorage pour tester
    const devTenant = localStorage.getItem('dev_tenant_slug')
    return devTenant || null
  }

  // Extraire le subdomain
  const parts = hostname.split('.')

  // terrilink.io (2 parts) → pas de subdomain
  if (parts.length < 3) {
    return null
  }

  // Prendre la première partie (subdomain)
  const subdomain = parts[0]

  // Vérifier si c'est un subdomain réservé
  if (RESERVED_SUBDOMAINS.includes(subdomain)) {
    return null
  }

  // Valider le format du slug (alphanumérique + tirets)
  const slugPattern = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/i
  if (!slugPattern.test(subdomain)) {
    console.warn(`[Tenant] Subdomain invalide détecté: "${subdomain}"`)
    return null
  }

  return subdomain
}

/**
 * Vérifie si on est sur l'application principale (app.terrilink.io)
 *
 * @returns {boolean} True si sur app.terrilink.io
 */
export function isAppDomain() {
  const hostname = window.location.hostname
  return hostname === `app.${BASE_DOMAIN}` || hostname === 'localhost' || hostname === '127.0.0.1'
}

/**
 * Vérifie si on est sur un domaine tenant (slug.terrilink.io)
 *
 * @returns {boolean} True si sur un tenant custom
 */
export function isTenantDomain() {
  return getTenantSlug() !== null
}

/**
 * Vérifie si on est sur la landing page (terrilink.io ou www.terrilink.io)
 *
 * @returns {boolean} True si sur la landing
 */
export function isLandingDomain() {
  const hostname = window.location.hostname
  return hostname === BASE_DOMAIN || hostname === `www.${BASE_DOMAIN}`
}

/**
 * Retourne le domaine de base de la plateforme
 *
 * @returns {string} "terrilink.io"
 */
export function getBaseDomain() {
  return BASE_DOMAIN
}

/**
 * Retourne le type de domaine actuel
 *
 * @returns {'landing'|'app'|'tenant'|'development'} Type de domaine
 */
export function getDomainType() {
  const hostname = window.location.hostname

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development'
  }

  if (isLandingDomain()) {
    return 'landing'
  }

  if (isAppDomain()) {
    return 'app'
  }

  if (isTenantDomain()) {
    return 'tenant'
  }

  return 'app' // Fallback
}

/**
 * Retourne des informations complètes sur le tenant actuel
 *
 * Utilisé pour le branding/UX uniquement (pas pour la sécurité)
 *
 * @returns {object} Informations tenant
 * @returns {string|null} .slug - Slug du tenant (pour branding)
 * @returns {string} .domainType - Type de domaine
 * @returns {boolean} .isCustomTenant - True si tenant custom
 * @returns {string} .hostname - Hostname complet
 */
export function getTenantInfo() {
  const slug = getTenantSlug()
  const domainType = getDomainType()

  return {
    slug,
    domainType,
    isCustomTenant: domainType === 'tenant',
    hostname: window.location.hostname,
  }
}

/**
 * Configure un tenant de test en développement local
 *
 * Utilisation: setDevTenant('aveyronworldwide')
 * Pour reset: setDevTenant(null)
 *
 * @param {string|null} slug - Slug du tenant à simuler
 */
export function setDevTenant(slug) {
  if (slug) {
    localStorage.setItem('dev_tenant_slug', slug)
    console.log(`[Tenant] Dev tenant configuré: "${slug}"`)
  } else {
    localStorage.removeItem('dev_tenant_slug')
    console.log('[Tenant] Dev tenant supprimé')
  }

  // Suggérer un reload pour appliquer les changements
  console.log('[Tenant] Rechargez la page pour appliquer les changements')
}

/**
 * Génère l'URL complète pour un tenant donné
 *
 * Exemples:
 * - getTenantUrl('aveyronworldwide') → "https://aveyronworldwide.terrilink.io"
 * - getTenantUrl('app') → "https://app.terrilink.io"
 *
 * @param {string} slug - Slug du tenant ou subdomain
 * @returns {string} URL complète
 */
export function getTenantUrl(slug) {
  const protocol = window.location.protocol // http: ou https:
  return `${protocol}//${slug}.${BASE_DOMAIN}`
}

// Export un objet par défaut pour usage groupé
export default {
  getTenantSlug,
  isAppDomain,
  isTenantDomain,
  isLandingDomain,
  getBaseDomain,
  getDomainType,
  getTenantInfo,
  setDevTenant,
  getTenantUrl,
}
